/**
 * Страница деталей одного проекта Производства — /v2/production/$projectId.
 * Три вкладки: Основное (редактируемая форма) | AmoCRM (полные поля сделки) | ДДС (расходы+приходы).
 */
import { lazy, Suspense, useState, type FormEvent } from "react";
import { Link, useParams, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth, hasAccess } from "@/auth/AuthContext";

import { Icon } from "@/components/prototype/Icon";
import { Spinner } from "@/components/Spinner";
import { ApiError } from "@/api/client";
import { parseBackendIso } from "@/lib/presence";
import {
  fetchProductionProject, fetchProductionAmo, fetchProductionCashflow,
  updateProductionProject, deleteProductionProject,
  addProductionNote, deleteProductionNote,
  fetchUsersByRole, ROLE_FIELD_ENGINEER,
  sendVoiceNote, fetchVisitsList,
  type ProductionProject, type ProductionPatch, type Visit,
} from "@/api/dashboard";
import { VoiceNoteRecorder, VoiceNotePlayer, extractVoiceAudioUrl } from "@/components/VoiceNoteRecorder";
import { formatRub, KIND_COLORS, KIND_LABELS, type CategoryKind } from "@/api/finance";


const VisitEditorModalLazy = lazy(() =>
  import("@/pages/VisitsPage").then((module) => ({ default: module.VisitEditorModal })),
);


type Tab = "main" | "amo" | "cashflow";


const MONTHS = [
  "Январь", "Февраль", "Март", "Апрель", "Май", "Июнь",
  "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь",
];
const PERIOD_OPTIONS: string[] = (() => {
  const out: string[] = [];
  for (let i = 0; i < MONTHS.length; i++) {
    out.push(MONTHS[i]);
    if (i < MONTHS.length - 1) out.push(`${MONTHS[i]}-${MONTHS[i + 1].toLowerCase()}`);
  }
  return out;
})();
void PERIOD_OPTIONS;

const STATUS_OPTIONS: [string, string][] = [
  ["in_progress",  "В работе"],
  ["completed",    "Выполнен"],
  ["not_signed",   "Не заключился"],
  ["awaiting",     "В ожидании"],
];


export function ProductionDetailPage() {
  const params = useParams({ strict: false }) as { projectId?: string };
  const projectId = Number(params.projectId);
  const [tab, setTab] = useState<Tab>("main");

  const projQ = useQuery({
    queryKey: ["production-project", projectId],
    queryFn: () => fetchProductionProject(projectId),
    enabled: !!projectId,
  });

  if (!projectId) return <div className="alert alert-red"><div className="alert-body">Неверный id проекта</div></div>;
  if (projQ.isLoading) return <Spinner size="lg" label="Загрузка проекта…"/>;
  if (projQ.error) {
    if (projQ.error instanceof ApiError && projQ.error.status === 404) {
      return <div className="alert alert-amber"><div className="alert-body">Проект не найден</div></div>;
    }
    return <div className="alert alert-red"><div className="alert-body">Ошибка: {(projQ.error as Error).message}</div></div>;
  }
  const p = projQ.data!;

  return (
    <div className="page" style={{ padding: 16 }}>
      {/* 2-колоночный layout как в AmoCRM:
          ЛЕВО — вся карточка сделки (шапка + табы + контент),
          ПРАВО — примечания. */}
      <div className="prod-detail-grid" style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 12,
        alignItems: "stretch",
      }}>
        {/* Левая колонка — карточка сделки */}
        <div style={{ display: "flex", flexDirection: "column", gap: 12, minWidth: 0 }}>
          {/* Шапка внутри левой колонки */}
          <div className="card" style={{ padding: "14px 16px" }}>
            {/* Строка 1: back + имя клиента + локация */}
            <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
              <Link to="/" className="btn btn-sm btn-ghost" title="Назад к списку">
                <Icon name="chevron_l" size={14}/>
              </Link>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>{p.client_name}</h1>
                {p.location && <div style={{ marginTop: 4, color: "var(--mute)", fontSize: 13 }}>📍 {p.location}</div>}
              </div>
            </div>

            {/* Строка 2: табы + статус + период + ссылка в Amo — всё одной строкой */}
            <div style={{
              marginTop: 12,
              display: "flex", alignItems: "center", gap: 8,
              flexWrap: "wrap",
            }}>
              <div className="chip-row" style={{ margin: 0 }}>
                {([
                  ["main",      "Основное"],
                  ["amo",       "AmoCRM"],
                  ["cashflow",  "ДДС"],
                ] as [Tab, string][]).map(([k, label]) => (
                  <button key={k} className={`chip ${tab === k ? "active" : ""}`} onClick={() => setTab(k)}>
                    {label}
                  </button>
                ))}
              </div>
              <StatusBadge status={p.status}/>
              {p.period_label && <span className="badge badge-mute">{p.period_label}</span>}
              {p.amo_lead_id && (
                <a href={p.client_amo_url ?? `https://kshagin2009.amocrm.ru/leads/detail/${p.amo_lead_id}`}
                   target="_blank" rel="noopener noreferrer"
                   className="btn btn-sm"
                   style={{ marginLeft: "auto", textDecoration: "none" }}>
                  <Icon name="external" size={11}/> AmoCRM (#{p.amo_lead_id})
                </a>
              )}
            </div>
          </div>

          {/* Контент выбранного таба */}
          {tab === "main" && <MainTab p={p}/>}
          {tab === "amo" && <AmoTab projectId={projectId}/>}
          {tab === "cashflow" && <CashflowTab projectId={projectId}/>}
        </div>

        {/* Правая колонка — примечания (вместо чата в AmoCRM) */}
        <NotesPanel p={p}/>
      </div>
    </div>
  );
}


/* ── Правая панель «Примечания» — AmoCRM-style фид + композер ─────────── */
type FeedItem =
  | { kind: "note"; idx: number; ts: string; author: string; text: string }
  | { kind: "amo"; ts: string; source: string; fields: Record<string, { old: unknown; new: unknown }> };

function NotesPanel({ p }: { p: ProductionProject }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<string>("");
  const [composerOpen, setComposerOpen] = useState(false);

  const add = useMutation({
    mutationFn: () => addProductionNote(p.id, draft),
    onSuccess: () => {
      setDraft("");
      setComposerOpen(false);
      qc.invalidateQueries({ queryKey: ["production-project", p.id] });
    },
  });
  const del = useMutation({
    mutationFn: (idx: number) => deleteProductionNote(p.id, idx),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["production-project", p.id] }),
  });

  async function sendVoice(args: { file: Blob; durationSec: number }) {
    if (!p.amo_lead_id) {
      alert("У карточки нет amo_lead_id — голосовое отправить нельзя.");
      return;
    }
    try {
      const res = await sendVoiceNote(p.amo_lead_id, args.file, args.durationSec, {
        category: "visits", propagateToProduction: true,
      });
      qc.invalidateQueries({ queryKey: ["production-project", p.id] });
      if (!res.amo_note_id) {
        alert("Голосовое сохранил, но в AmoCRM не получилось — проверь токен.");
      }
    } catch (e) {
      alert(`Ошибка голосового: ${e instanceof Error ? e.message : String(e)}`);
    }
  }

  // Собираем фид: notes_log + update_log, сортируем по ts DESC
  const feed: FeedItem[] = [
    ...(p.notes_log ?? []).map((n, i) => ({
      kind: "note" as const, idx: i, ts: n.ts, author: n.author_name, text: n.text,
    })),
    ...(p.update_log ?? []).map(u => ({
      kind: "amo" as const, ts: u.ts, source: u.source, fields: u.fields,
    })),
  ].sort((a, b) => (b.ts || "").localeCompare(a.ts || ""));

  function fmt(ts: string): string {
    const d = parseBackendIso(ts) ?? new Date(ts);
    return d.toLocaleString("ru", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  }

  return (
    <aside className="card notes-panel" style={{
      display: "flex", flexDirection: "column",
      padding: 0,
      position: "sticky", top: 12,
      height: "calc(100vh - 24px)",
      alignSelf: "start",
      overflow: "hidden",
    }}>
      {/* Шапка */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "12px 14px", borderBottom: "1px solid #f0f0f0",
      }}>
        <h3 style={{ margin: 0, fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.4px", color: "#707579" }}>
          Примечания и события
        </h3>
        <span style={{ fontSize: 11, color: "#a0a4a8" }}>{feed.length}</span>
      </div>

      {/* Фид (скролл) */}
      <div style={{
        flex: 1, minHeight: 0,
        overflowY: "auto",
        padding: "10px 14px",
        display: "flex", flexDirection: "column", gap: 8,
      }}>
        {feed.length === 0 && (
          <div style={{ color: "#a0a4a8", fontSize: 13, textAlign: "center", padding: "20px 0" }}>
            Пока нет ни примечаний, ни истории.
          </div>
        )}
        {feed.map((item, i) => (
          <FeedCard key={i} item={item} fmt={fmt}
                    onDelete={item.kind === "note" ? () => del.mutate(item.idx) : undefined}/>
        ))}
      </div>

      {/* Композер */}
      <div style={{
        borderTop: "1px solid #f0f0f0",
        padding: 12,
        display: "flex", flexDirection: "column", gap: 8,
        background: "#fafafa",
      }}>
        {!composerOpen ? (
          <div style={{ display: "flex", gap: 8, alignItems: "stretch" }}>
            <button
              type="button"
              onClick={() => setComposerOpen(true)}
              style={{
                flex: 1,
                background: "transparent", border: "1px dashed #d0d2d4",
                color: "#707579", borderRadius: 8, padding: "8px 12px",
                cursor: "pointer", fontSize: 13, textAlign: "left",
                fontFamily: "inherit",
              }}
            >
              + Добавить примечание
            </button>
            {p.amo_lead_id && (
              <VoiceNoteRecorder onSend={sendVoice} accent="#4fae4e" idleTitle="Записать голосовое"/>
            )}
          </div>
        ) : (
          <>
            <textarea
              className="input"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Текст примечания…"
              autoFocus
              style={{
                resize: "vertical", minHeight: 70,
                fontFamily: "inherit", lineHeight: 1.5, padding: 10,
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.ctrlKey || e.metaKey) && draft.trim()) {
                  e.preventDefault();
                  add.mutate();
                }
              }}
            />
            <div style={{ display: "flex", gap: 8 }}>
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={() => add.mutate()}
                disabled={add.isPending || !draft.trim()}
                style={{ flex: 1 }}
              >
                {add.isPending ? "…" : "Добавить"}
              </button>
              {p.amo_lead_id && (
                <VoiceNoteRecorder onSend={sendVoice} disabled={add.isPending} accent="#4fae4e"/>
              )}
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => { setComposerOpen(false); setDraft(""); }}
                disabled={add.isPending}
              >
                Отменить
              </button>
            </div>
            <div style={{ fontSize: 10, color: "#a0a4a8" }}>Ctrl+Enter — отправить</div>
          </>
        )}
      </div>
    </aside>
  );
}

function FeedCard({ item, fmt, onDelete }: {
  item: FeedItem;
  fmt: (ts: string) => string;
  onDelete?: () => void;
}) {
  const isNote = item.kind === "note";
  return (
    <div style={{
      background: isNote ? "#fffdf0" : "#f7f8fa",
      border: `1px solid ${isNote ? "#f0e6c2" : "#eaecef"}`,
      borderRadius: 10,
      padding: "8px 10px",
      fontSize: 12,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
        <span style={{
          padding: "1px 6px", borderRadius: 4,
          fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.3px",
          background: isNote ? "#e8c87c" : "#d8dde3",
          color: isNote ? "#5a4502" : "#4a5060",
        }}>
          {isNote ? "Примечание" : "Amo"}
        </span>
        <span style={{ color: "#707579", fontSize: 11 }}>{fmt(item.ts)}</span>
        {isNote && (
          <span style={{ color: "#1a1a1a", fontSize: 11, fontWeight: 600, marginLeft: "auto" }}>
            {item.author}
          </span>
        )}
        {onDelete && (
          <button
            type="button"
            onClick={onDelete}
            title="Удалить примечание"
            style={{
              background: "transparent", border: "none", padding: 0, marginLeft: 4,
              color: "#a0a4a8", cursor: "pointer", fontSize: 14, lineHeight: 1,
            }}
          >×</button>
        )}
      </div>
      {isNote ? (
        <>
          <div style={{ color: "#1a1a1a", whiteSpace: "pre-wrap", lineHeight: 1.5 }}>
            {item.text}
          </div>
          {(() => {
            const audio = extractVoiceAudioUrl(item.text ?? "");
            return audio ? <VoiceNotePlayer src={audio}/> : null;
          })()}
        </>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {Object.entries(item.fields).map(([k, v]) => (
            <div key={k} style={{ color: "#1a1a1a", lineHeight: 1.4 }}>
              <span style={{ color: "#707579" }}>{k}:</span>{" "}
              <span style={{ textDecoration: "line-through", color: "#a0a4a8" }}>
                {String((v as { old: unknown }).old ?? "—")}
              </span>
              {" → "}
              <span style={{ fontWeight: 600 }}>
                {String((v as { new: unknown }).new ?? "—")}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; bg: string; fg: string }> = {
    in_progress: { label: "В работе",   bg: "rgba(178,52,40,0.12)",  fg: "var(--brick)" },
    completed:   { label: "Готов",      bg: "rgba(61,122,61,0.12)",  fg: "var(--green)" },
    awaiting:    { label: "Ожидает",    bg: "rgba(201,122,31,0.15)", fg: "#a85f12" },
    not_signed:  { label: "Без договора", bg: "rgba(120,140,150,0.10)", fg: "var(--mute)" },
  };
  const s = map[status] || { label: status, bg: "rgba(120,140,150,0.10)", fg: "var(--mute)" };
  return (
    <span style={{ padding: "3px 10px", borderRadius: 12, background: s.bg, color: s.fg, fontSize: 11, fontWeight: 600 }}>
      {s.label}
    </span>
  );
}


/* ── Таб «Основное» — редактируемая форма ─────────────────────── */
function MainTab({ p }: { p: ProductionProject }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { user: authUser } = useAuth();

  const [data, setData] = useState<ProductionPatch>({
    client_name: p.client_name, client_amo_url: p.client_amo_url,
    period_label: p.period_label, status: p.status, work_dates: p.work_dates,
    completed_year: p.completed_year, completed_date: p.completed_date,
    sum_contract: p.sum_contract, sum_expected: p.sum_expected, sum_actual: p.sum_actual,
    payment_type: p.payment_type, location: p.location,
    what_done: p.what_done, brick_color: p.brick_color, brick_count: p.brick_count,
    gazebo_size: p.gazebo_size, accommodation: p.accommodation,
    water_source: p.water_source, needs_pump: p.needs_pump,
    comment: p.comment,
    field_engineer_user_id: p.field_engineer_user_id,
  });

  // Список выездных инженеров для селекта
  const engineersQ = useQuery({
    queryKey: ["users-by-role", ROLE_FIELD_ENGINEER],
    queryFn: () => fetchUsersByRole(ROLE_FIELD_ENGINEER),
    staleTime: 5 * 60_000,
  });

  function set<K extends keyof ProductionPatch>(k: K, v: ProductionPatch[K]) {
    setData(d => ({ ...d, [k]: v }));
  }

  const save = useMutation({
    mutationFn: () => updateProductionProject(p.id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["production-project", p.id] });
      qc.invalidateQueries({ queryKey: ["production"] });
    },
    onError: (e: Error) => alert(`Ошибка: ${e.message}`),
  });

  const del = useMutation({
    mutationFn: () => deleteProductionProject(p.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["production"] });
      void navigate({ to: "/" });
    },
    onError: (e: Error) => alert(`Ошибка: ${e.message}`),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    save.mutate();
  }

  return (
    <form onSubmit={onSubmit} className="card" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Производственная карточка</h3>
        <span style={{ fontSize: 11, color: "var(--mute)" }}>
          Создан: {(parseBackendIso(p.created_at) ?? new Date(p.created_at)).toLocaleString("ru")} · обновлён: {(parseBackendIso(p.updated_at) ?? new Date(p.updated_at)).toLocaleString("ru")}
        </span>
      </div>

      <div className="prod-form-row">
        <FormField label="Клиент" required>
          <input className="input" value={data.client_name ?? ""} onChange={(e) => set("client_name", e.target.value)} required/>
        </FormField>
      </div>
      <div className="prod-form-row prod-form-grid-3">
        <FormField label="Выполнен — год" hint="Только год, если точной даты нет">
          <input className="input" type="number" placeholder="напр. 2026" min={2000} max={2099}
                 value={data.completed_year ?? ""}
                 onChange={(e) => set("completed_year", e.target.value ? Number(e.target.value) : null)}/>
        </FormField>
        <FormField label="Выполнен — дата" hint="Полная дата, если знаете">
          <input className="input" type="date"
                 value={data.completed_date ?? ""}
                 onChange={(e) => set("completed_date", e.target.value || null)}/>
        </FormField>
        <FormField label="Статус">
          <select
            className="input"
            value={data.status ?? "in_progress"}
            onChange={(e) => {
              const newStatus = e.target.value;
              // Warning: ставят «Выполнен», а в Amo сделка ещё не на финальной
              // стадии «Заказ выполнен / Акт подписан». Без подтверждения
              // легко закрыть Икшу преждевременно и потерять его из Сопровождения.
              const amoStage = (p as { amo_canonical_stage?: string | null }).amo_canonical_stage ?? null;
              if (newStatus === "completed" && amoStage && amoStage !== "Заказ выполнен / Акт подписан") {
                const ok = confirm(
                  `⚠ В Amo сделка сейчас в стадии «${amoStage}», ещё не «Заказ выполнен / Акт подписан».\n\n` +
                  `Если поставить «Выполнен» — сделка пропадёт из Сопровождения. Точно завершить?`,
                );
                if (!ok) return;
              }
              set("status", newStatus);
            }}
          >
            {STATUS_OPTIONS.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
          </select>
        </FormField>
      </div>
      {/* Поля «Выездной инженер» и «Ответственный менеджер ОП» — показываем
          ТОЛЬКО для pre-contract сделок (status='lead_stub'). Как только
          сделка перешла за «Договор заключён» / «Договор отложенный»
          (status стал in_progress / completed / awaiting / cancelled),
          выездная стадия УЖЕ пройдена — поля не несут смысла и захламляют
          карточку (пользователь жалуется: «Икша в Выездах, хотя договор
          подписан»). */}
      {p.status === "lead_stub" && (
        <FormField label="Выездной инженер" hint="Для расчёта мотивации инженера от приходов по этой сделке">
          <select className="input"
                  value={data.field_engineer_user_id ?? ""}
                  onChange={(e) => set("field_engineer_user_id", e.target.value || null)}
                  disabled={engineersQ.isLoading}>
            <option value="">— не выбран —</option>
            {(engineersQ.data ?? []).map(u => (
              <option key={u.id} value={u.id}>{u.display_name || "Без имени"}</option>
            ))}
            {data.field_engineer_user_id && !(engineersQ.data ?? []).some(u => u.id === data.field_engineer_user_id) && (
              <option value={data.field_engineer_user_id}>(вне списка) {p.field_engineer_name ?? data.field_engineer_user_id}</option>
            )}
          </select>
          {p.field_engineer_suggested_user_id
            && p.field_engineer_suggested_user_id !== (data.field_engineer_user_id ?? null) && (
            <div style={{
              marginTop: 6, padding: "8px 12px",
              background: "rgba(232,200,80,0.18)", borderLeft: "3px solid #a85f12",
              borderRadius: 4, fontSize: 12, color: "#7a4500",
            }}>
              ⚠ Согласно «Расписанию выездов» по этой сделке выезжал{" "}
              <strong>{p.field_engineer_suggested_name}</strong>.
              {" "}
              <button type="button"
                      onClick={() => set("field_engineer_user_id", p.field_engineer_suggested_user_id)}
                      style={{
                        background: "transparent", border: "none", padding: 0,
                        color: "var(--brick)", textDecoration: "underline", cursor: "pointer",
                        fontSize: 12,
                      }}>
                Поставить его
              </button>
            </div>
          )}
        </FormField>
      )}
      {p.status === "lead_stub" && (
        <FormField label="Ответственный менеджер ОП" hint="Из расписания выездов (по amo_lead_id)">
          <div style={{
            padding: "8px 12px", background: "var(--paper-2, #f6f7f9)",
            border: "1px solid var(--line, #e6e6e6)", borderRadius: 6,
            fontSize: 14, color: p.sales_manager_name ? "var(--ink)" : "var(--mute)",
          }}>
            {p.sales_manager_name ?? "— нет выезда / Amo не вернул менеджера —"}
          </div>
        </FormField>
      )}
      {p.drive_topic_id && p.drive_folder_id && (
        <FormField label="Папка на «Диске»" hint="Папка сделки в хранилище — фото, документы, замеры">
          <a
            href={(() => {
              const h = typeof window !== "undefined" ? window.location.hostname : "";
              // На messenger-сабдомене nginx делает 301 с / на /messenger,
              // но БЕЗ сохранения query string — поэтому ведём сразу на
              // /messenger, query сохраняется.
              const messengerHost = h.replace(/^(production|sales|warehouse|finance)\./, "messenger.");
              return `https://${messengerHost}/messenger?topic=${p.drive_topic_id}&folder=${p.drive_folder_id}`;
            })()}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              padding: "8px 14px",
              background: "#fff", border: "1px solid #d4d4d8",
              borderRadius: 6, fontSize: 13, fontWeight: 500,
              color: "var(--brick, #a63a1f)", textDecoration: "none",
              transition: "background 120ms, border-color 120ms",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#fef2ef";
              e.currentTarget.style.borderColor = "var(--brick, #a63a1f)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "#fff";
              e.currentTarget.style.borderColor = "#d4d4d8";
            }}
          >
            📂 Открыть папку на Диске
            <span style={{ fontSize: 11, color: "var(--mute)" }}>↗</span>
          </a>
        </FormField>
      )}
      {p.amo_lead_id && (
        <FormField label="Выезды" hint="Те же выезды, что в «Расписании выездов» — фильтр по amo_lead_id">
          <VisitsForLead amoLeadId={p.amo_lead_id} clientName={p.client_name} clientAmoUrl={p.client_amo_url}/>
        </FormField>
      )}
      <FormField label="Даты работ">
        <DateRangeInput value={data.work_dates ?? ""} onChange={(v) => set("work_dates", v)}/>
      </FormField>
      <div className="prod-form-row prod-form-grid-3">
        <FormField label="Сумма договора (₽)">
          <MoneyInput value={data.sum_contract} onChange={(v) => set("sum_contract", v)}/>
        </FormField>
        <FormField label="Ожидаемая (₽)">
          <MoneyInput value={data.sum_expected} onChange={(v) => set("sum_expected", v)}/>
        </FormField>
        <FormField label="Фактическая (₽)">
          <MoneyInput value={data.sum_actual} onChange={(v) => set("sum_actual", v)}/>
        </FormField>
      </div>
      <div className="prod-form-row prod-form-grid-2">
        <FormField label="Оплата">
          <select className="input" value={data.payment_type ?? ""} onChange={(e) => set("payment_type", e.target.value || null)}>
            <option value="">—</option>
            <option value="cash">Налички</option>
            <option value="non_cash">Безнал</option>
          </select>
        </FormField>
        <FormField label="Локация"><input className="input" value={data.location ?? ""} onChange={(e) => set("location", e.target.value)}/></FormField>
      </div>
      <FormField label="Что уже сделали"><input className="input" value={data.what_done ?? ""} onChange={(e) => set("what_done", e.target.value)}/></FormField>
      <div className="prod-form-row prod-form-grid-3">
        <FormField label="Цвет кирпича"><input className="input" placeholder="Яшма / Агат" value={data.brick_color ?? ""} onChange={(e) => set("brick_color", e.target.value)}/></FormField>
        <FormField label="Кол-во шт"><input className="input" type="number" value={data.brick_count ?? ""} onChange={(e) => set("brick_count", e.target.value ? parseInt(e.target.value, 10) : null)}/></FormField>
        <FormField label="Размер беседки"><input className="input" placeholder="5.0×6.0" value={data.gazebo_size ?? ""} onChange={(e) => set("gazebo_size", e.target.value)}/></FormField>
      </div>
      <FormField label="Проживание"><input className="input" value={data.accommodation ?? ""} onChange={(e) => set("accommodation", e.target.value)}/></FormField>
      <div className="prod-form-row prod-form-grid-2">
        <FormField label="Вода"><input className="input" value={data.water_source ?? ""} onChange={(e) => set("water_source", e.target.value)}/></FormField>
        <FormField label="Насос"><input className="input" value={data.needs_pump ?? ""} onChange={(e) => set("needs_pump", e.target.value)}/></FormField>
      </div>
      <FormField label="Ссылка на AmoCRM">
        <input className="input" type="url" placeholder="https://kshagin2009.amocrm.ru/leads/detail/…" value={data.client_amo_url ?? ""} onChange={(e) => set("client_amo_url", e.target.value)}/>
      </FormField>

      {p.update_log && p.update_log.length > 0 && (
        <details style={{ marginTop: 12, marginBottom: 8, fontSize: 12 }}>
          <summary style={{ cursor: "pointer", color: "var(--mute)", padding: "6px 0" }}>📜 История изменений из Amo · {p.update_log.length}</summary>
          <div style={{ marginTop: 8, padding: 10, background: "var(--paper-2)", borderRadius: 6 }}>
            {p.update_log.slice().reverse().map((entry, i) => (
              <div key={i} style={{ marginBottom: 8, paddingBottom: 8, borderBottom: "1px solid var(--line)" }}>
                <div style={{ color: "var(--mute)", fontSize: 11 }}>{new Date(entry.ts).toLocaleString("ru-RU")} · {entry.source}</div>
                {Object.entries(entry.fields).map(([k, v]) => (
                  <div key={k} style={{ marginTop: 2 }}>
                    <span style={{ color: "var(--mute)" }}>{k}:</span>{" "}
                    <span style={{ textDecoration: "line-through", color: "var(--mute)" }}>{String((v as { old: unknown }).old ?? "—")}</span>{" → "}
                    <span style={{ color: "var(--brick-soft)" }}>{String((v as { new: unknown }).new ?? "—")}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </details>
      )}

      <div className="prod-form-actions" style={{ marginTop: 4 }}>
        {hasAccess(authUser, "delete-production") && (
          <button
            type="button"
            className="btn btn-sm"
            style={{ color: "var(--brick)", borderColor: "var(--brick)" }}
            onClick={() => { if (confirm("Удалить карточку?")) del.mutate(); }}
            disabled={del.isPending}
          >
            <Icon name="trash" size={11}/> {del.isPending ? "…" : "Удалить"}
          </button>
        )}
        <button type="submit" className="btn btn-sm btn-primary" disabled={save.isPending} style={{ marginLeft: "auto" }}>
          {save.isPending ? "Сохранение…" : save.isSuccess ? "✓ Сохранено" : "Сохранить"}
        </button>
      </div>
    </form>
  );
}


/* ── Helpers для формы ─────────────────────────────────────────── */

function FormField({ label, required, hint, children }: { label: string; required?: boolean; hint?: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "block" }}>
      <div style={{ fontSize: 11, color: "var(--mute)", marginBottom: 4, letterSpacing: "0.04em", textTransform: "uppercase" }}>
        {label}{required && <span style={{ color: "var(--brick)" }}> *</span>}
      </div>
      {children}
      {hint && <div style={{ fontSize: 11, color: "var(--mute)", marginTop: 4, textTransform: "none", letterSpacing: 0 }}>{hint}</div>}
    </label>
  );
}

function MoneyInput({ value, onChange }: { value: number | null | undefined; onChange: (v: number | null) => void }) {
  const display = value !== null && value !== undefined && !Number.isNaN(value)
    ? new Intl.NumberFormat("ru-RU").format(value)
    : "";
  return (
    <input
      className="input"
      type="text"
      inputMode="numeric"
      value={display}
      placeholder="0"
      onChange={(e) => {
        const raw = e.target.value.replace(/[^\d]/g, "");
        onChange(raw ? parseInt(raw, 10) : null);
      }}
    />
  );
}

function DateRangeInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  // value: "DD.MM.YYYY — DD.MM.YYYY"; любая сторона может быть пустой.
  // Парсим каждую сторону НЕЗАВИСИМО — иначе одиночная дата не удерживается
  // (строка "15.05.2026 — " не матчила парную регулярку и дата стиралась).
  const toIso = (s: string): string => {
    const m = (s || "").trim().match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
    return m ? `${m[3]}-${m[2]}-${m[1]}` : "";
  };
  const fromIso = (iso: string): string => {
    if (!iso) return "";
    const [y, m, d] = iso.split("-");
    return `${d}.${m}.${y}`;
  };
  const dash = value.indexOf("—");
  const fromISO = toIso(dash >= 0 ? value.slice(0, dash) : value);
  const toISO = dash >= 0 ? toIso(value.slice(dash + 1)) : "";

  function update(newFrom: string, newTo: string) {
    const f = newFrom ? fromIso(newFrom) : "";
    const t = newTo ? fromIso(newTo) : "";
    onChange(f || t ? `${f} — ${t}` : "");
  }

  return (
    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
      <input className="input" type="date" value={fromISO} onChange={(e) => update(e.target.value, toISO)} style={{ flex: 1 }}/>
      <span style={{ color: "var(--mute)" }}>—</span>
      <input className="input" type="date" value={toISO} onChange={(e) => update(fromISO, e.target.value)} style={{ flex: 1 }}/>
    </div>
  );
}


/* ── Таб «AmoCRM» ──────────────────────────────────────────────── */
type AmoLeadCustomField = {
  field_id?: number;
  field_name?: string;
  field_code?: string;
  field_type?: string;
  values?: Array<{ value?: unknown; enum_id?: number; enum_code?: string }>;
};
type AmoEmbedded = { contacts?: Array<Record<string, unknown>> };
type AmoLead = {
  id?: number;
  name?: string;
  price?: number;
  status_id?: number;
  pipeline_id?: number;
  responsible_user_id?: number;
  created_at?: number;
  updated_at?: number;
  closed_at?: number | null;
  closest_task_at?: number | null;
  custom_fields_values?: AmoLeadCustomField[];
  _embedded?: AmoEmbedded;
};

function AmoTab({ projectId }: { projectId: number }) {
  const q = useQuery({
    queryKey: ["production-amo", projectId],
    queryFn: () => fetchProductionAmo(projectId),
    staleTime: 60 * 1000,
  });
  if (q.isLoading) return <div className="card"><div className="muted" style={{ padding: 14 }}>Тянем данные из AmoCRM…</div></div>;
  if (q.error) return <div className="alert alert-red"><div className="alert-body">Ошибка: {(q.error as Error).message}</div></div>;
  const data = q.data!;
  if (data.error) return <div className="alert alert-amber"><div className="alert-body">{data.error}</div></div>;
  const lead = data.lead as AmoLead | undefined;
  if (!lead) return <div className="alert alert-amber"><div className="alert-body">Нет данных</div></div>;

  const fmtTs = (ts?: number | null) => ts ? new Date(ts * 1000).toLocaleString("ru") : "—";
  const customFields = lead.custom_fields_values || [];
  const contacts = lead._embedded?.contacts || [];

  const Row = ({ k, v }: { k: string; v: React.ReactNode }) => (
    <div style={{ display: "grid", gridTemplateColumns: "240px 1fr", gap: 16, padding: "6px 0", borderBottom: "1px solid var(--line)", fontSize: 13 }}>
      <span style={{ color: "var(--mute)" }}>{k}</span>
      <span style={{ color: "var(--ink-1)" }}>{v ?? <span className="muted">—</span>}</span>
    </div>
  );

  return (
    <>
      <div className="card" style={{ marginBottom: "var(--gap)" }}>
        <h3 style={{ margin: "0 0 12px", fontSize: 16, fontWeight: 700 }}>Сделка в AmoCRM</h3>
        <Row k="ID лида"             v={lead.id}/>
        <Row k="Название"            v={lead.name}/>
        <Row k="Бюджет"              v={lead.price ? formatRub(lead.price) : null}/>
        <Row k="Статус (status_id)"  v={lead.status_id}/>
        <Row k="Воронка (pipeline)"  v={lead.pipeline_id}/>
        <Row k="Ответственный (id)"  v={lead.responsible_user_id}/>
        <Row k="Создан"              v={fmtTs(lead.created_at)}/>
        <Row k="Обновлён"            v={fmtTs(lead.updated_at)}/>
        <Row k="Закрыт"              v={lead.closed_at ? fmtTs(lead.closed_at) : null}/>
        <Row k="Ближайшая задача"    v={lead.closest_task_at ? fmtTs(lead.closest_task_at) : null}/>
      </div>

      {customFields.length > 0 && (
        <div className="card" style={{ marginBottom: "var(--gap)" }}>
          <h3 style={{ margin: "0 0 12px", fontSize: 16, fontWeight: 700 }}>Кастомные поля ({customFields.length})</h3>
          {customFields.map((cf, i) => {
            const vals = (cf.values || []).map(v => {
              if (typeof v.value === "boolean") return v.value ? "✓" : "—";
              if (v.value === null || v.value === undefined) return "—";
              return String(v.value);
            }).join(", ");
            return <Row key={i} k={cf.field_name || `field_${cf.field_id}`} v={vals}/>;
          })}
        </div>
      )}

      {contacts.length > 0 && (
        <div className="card">
          <h3 style={{ margin: "0 0 12px", fontSize: 16, fontWeight: 700 }}>Контакты ({contacts.length})</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {contacts.map((c, i) => <ContactCard key={(c.id as number) ?? i} c={c}/>)}
          </div>
        </div>
      )}
    </>
  );
}


function ContactCard({ c }: { c: Record<string, unknown> }) {
  const id = c.id as number | undefined;
  const name = (c.name as string) || (c.first_name as string) || "Без имени";
  const isMain = c.is_main === true;
  const cf = (c.custom_fields_values as Array<{ field_code?: string; field_name?: string; values?: Array<{ value?: unknown; enum?: string }> }> | undefined) || [];

  // Из custom fields обычно: PHONE (тип phone), EMAIL (тип email), POSITION
  const phones: Array<{ enum?: string; value: string }> = [];
  const emails: Array<{ enum?: string; value: string }> = [];
  const other: Array<{ name: string; value: string }> = [];

  for (const f of cf) {
    const code = f.field_code;
    const vals = f.values || [];
    if (code === "PHONE") {
      vals.forEach(v => v.value && phones.push({ enum: v.enum, value: String(v.value) }));
    } else if (code === "EMAIL") {
      vals.forEach(v => v.value && emails.push({ enum: v.enum, value: String(v.value) }));
    } else if (f.field_name) {
      const txt = vals.map(v => v.value).filter(Boolean).join(", ");
      if (txt) other.push({ name: f.field_name, value: txt });
    }
  }

  const responsible = c.responsible_user_id as number | undefined;
  const createdAt = c.created_at as number | undefined;

  return (
    <div style={{ border: "1px solid var(--line)", borderRadius: 8, padding: "12px 14px", background: "var(--paper-2)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
        <strong style={{ fontSize: 14 }}>{name}</strong>
        {isMain && (
          <span style={{ fontSize: 10, fontWeight: 600, color: "var(--green)", background: "rgba(61,122,61,0.1)", padding: "2px 8px", borderRadius: 10 }}>
            Главный
          </span>
        )}
        {id && (
          <a href={`https://kshagin2009.amocrm.ru/contacts/detail/${id}`} target="_blank" rel="noopener noreferrer"
             className="btn btn-sm" style={{ marginLeft: "auto" }}>
            <Icon name="external" size={11}/> #{id}
          </a>
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: "6px 14px", fontSize: 13 }}>
        {phones.length > 0 && (
          <>
            <span style={{ color: "var(--mute)" }}>Телефон{phones.length > 1 ? "ы" : ""}</span>
            <span>
              {phones.map((p, i) => (
                <a key={i} href={`tel:${p.value.replace(/[^\d+]/g, "")}`} style={{ color: "var(--ink-1)", marginRight: 12 }}>
                  {p.value}{p.enum && <span style={{ color: "var(--mute)", fontSize: 11 }}> · {p.enum.toLowerCase()}</span>}
                </a>
              ))}
            </span>
          </>
        )}
        {emails.length > 0 && (
          <>
            <span style={{ color: "var(--mute)" }}>E-mail</span>
            <span>
              {emails.map((e, i) => (
                <a key={i} href={`mailto:${e.value}`} style={{ color: "var(--ink-1)", marginRight: 12 }}>
                  {e.value}{e.enum && <span style={{ color: "var(--mute)", fontSize: 11 }}> · {e.enum.toLowerCase()}</span>}
                </a>
              ))}
            </span>
          </>
        )}
        {other.map((o, i) => (
          <span key={i} style={{ display: "contents" }}>
            <span style={{ color: "var(--mute)" }}>{o.name}</span>
            <span>{o.value}</span>
          </span>
        ))}
        {responsible && (
          <>
            <span style={{ color: "var(--mute)" }}>Ответственный</span>
            <span>id {responsible}</span>
          </>
        )}
        {createdAt && (
          <>
            <span style={{ color: "var(--mute)" }}>Создан</span>
            <span>{new Date(createdAt * 1000).toLocaleString("ru")}</span>
          </>
        )}
      </div>
    </div>
  );
}


/* ── Таб «ДДС» ─────────────────────────────────────────────────── */
function CashflowTab({ projectId }: { projectId: number }) {
  const { user } = useAuth();
  const q = useQuery({
    queryKey: ["production-cashflow", projectId],
    queryFn: () => fetchProductionCashflow(projectId),
    staleTime: 60 * 1000,
  });
  if (q.isLoading) return <div className="card"><div className="muted" style={{ padding: 14 }}>Загрузка ДДС…</div></div>;
  if (q.error) return <div className="alert alert-red"><div className="alert-body">{(q.error as Error).message}</div></div>;
  const d = q.data!;
  // Расходы и сальдо видят только собственники/админы (is_superuser);
  // менеджеры — только поступления.
  const canSeeExpenses = !!user?.is_superuser;

  if (!d.linked_object_id) {
    return <div className="alert alert-amber"><div className="alert-body">Нет связи с финансовым объектом — нет данных по ДДС</div></div>;
  }

  return (
    <>
      {/* KPI */}
      <div className={canSeeExpenses ? "row row-3" : ""} style={{ marginBottom: "var(--gap)" }}>
        <div className="kpi good">
          <div className="kpi-label">Поступления</div>
          <div className="kpi-value kpi-value-sm">{formatRub(d.totals.income_total)}</div>
          <div className="kpi-foot"><span style={{ fontSize: 12, color: "var(--mute)" }}>{d.totals.income_count} операций</span></div>
        </div>
        {canSeeExpenses && (
          <div className="kpi">
            <div className="kpi-label">Расходы</div>
            <div className="kpi-value kpi-value-sm" style={{ color: "var(--brick)" }}>{formatRub(d.totals.expense_total)}</div>
            <div className="kpi-foot"><span style={{ fontSize: 12, color: "var(--mute)" }}>{d.totals.expense_count} операций</span></div>
          </div>
        )}
        {canSeeExpenses && (
          <div className="kpi">
            <div className="kpi-label">Сальдо</div>
            <div className="kpi-value kpi-value-sm" style={{ color: d.totals.balance >= 0 ? "var(--green)" : "var(--brick)" }}>
              {d.totals.balance >= 0 ? "+" : ""}{formatRub(d.totals.balance)}
            </div>
          </div>
        )}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {/* Приходы */}
        <div className="card" style={{ minWidth: 0 }}>
          <h3 style={{ margin: "0 0 12px", fontSize: 14, fontWeight: 700, color: "var(--green)" }}>Приходы · {d.revenues.length}</h3>
          {d.revenues.length === 0 ? (
            <div className="muted" style={{ fontSize: 12 }}>Поступлений нет</div>
          ) : (
            <table className="tbl" style={{ fontSize: 12 }}>
              <thead>
                <tr>
                  <th style={{ width: 80 }}>Дата</th>
                  <th>Контрагент</th>
                  <th className="num" style={{ width: 100 }}>Сумма</th>
                </tr>
              </thead>
              <tbody>
                {d.revenues.map(r => (
                  <tr key={r.id}>
                    <td className="tabular">{r.received_at}</td>
                    <td>{r.counterparty || <span className="muted">—</span>}</td>
                    <td className="num strong tabular" style={{ color: "var(--green)" }}>{formatRub(r.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Расходы — только собственникам/админам */}
        {canSeeExpenses && (
        <div className="card" style={{ minWidth: 0 }}>
          <h3 style={{ margin: "0 0 12px", fontSize: 14, fontWeight: 700, color: "var(--brick)" }}>Расходы · {d.expenses.length}</h3>
          {d.expenses.length === 0 ? (
            <div className="muted" style={{ fontSize: 12 }}>Расходов нет</div>
          ) : (
            <table className="tbl" style={{ fontSize: 12 }}>
              <thead>
                <tr>
                  <th style={{ width: 80 }}>Дата</th>
                  <th>Статья</th>
                  <th style={{ width: 100 }}>Категория</th>
                  <th className="num" style={{ width: 100 }}>Сумма</th>
                </tr>
              </thead>
              <tbody>
                {d.expenses.map(e => (
                  <tr key={e.id}>
                    <td className="tabular">{e.date}</td>
                    <td>
                      <strong>{e.category_name || "—"}</strong>
                      {e.description && <div style={{ fontSize: 10, color: "var(--mute)" }}>{e.description}</div>}
                    </td>
                    <td>
                      {e.category_kind && (
                        <span style={{
                          background: KIND_COLORS[e.category_kind as CategoryKind] || "var(--mute)",
                          color: "#fff", fontSize: 10, fontWeight: 600,
                          padding: "2px 8px", borderRadius: 10,
                        }}>
                          {KIND_LABELS[e.category_kind as CategoryKind] || e.category_kind}
                        </span>
                      )}
                    </td>
                    <td className="num strong tabular" style={{ color: "var(--brick)" }}>{formatRub(e.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        )}
      </div>
    </>
  );
}


/* ── Секция «Выезды» — встроенный список + создание/редактирование ─── */
function VisitsForLead({ amoLeadId, clientName, clientAmoUrl }: {
  amoLeadId: number;
  clientName: string;
  clientAmoUrl: string | null;
}) {
  const qc = useQueryClient();
  const [modalTarget, setModalTarget] = useState<Visit | "new" | null>(null);
  const q = useQuery({
    queryKey: ["visits-by-lead", amoLeadId],
    queryFn: () => fetchVisitsList({ amo_lead_id: amoLeadId }),
    staleTime: 30_000,
  });
  const items = q.data?.items ?? [];

  function onSaved() {
    setModalTarget(null);
    qc.invalidateQueries({ queryKey: ["visits-by-lead", amoLeadId] });
    qc.invalidateQueries({ queryKey: ["visits-funnel"] });
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {q.isLoading ? (
        <div style={{
          padding: "10px 12px", fontSize: 12, color: "var(--mute)",
          background: "var(--paper-2, #f6f7f9)", borderRadius: 8,
        }}>Загрузка…</div>
      ) : items.length === 0 ? (
        <button
          type="button"
          onClick={() => setModalTarget("new")}
          style={{
            padding: "10px 12px", fontSize: 13, textAlign: "left",
            background: "var(--paper-2, #f6f7f9)",
            border: "1px dashed var(--line, #e6e6e6)", borderRadius: 8,
            color: "var(--mute)", cursor: "pointer",
            fontFamily: "inherit",
            transition: "color 100ms, border-color 100ms",
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLButtonElement).style.color = "var(--ink-2, #444)";
            (e.currentTarget as HTMLButtonElement).style.borderColor = "var(--brick, #a63a1f)";
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.color = "var(--mute)";
            (e.currentTarget as HTMLButtonElement).style.borderColor = "var(--line, #e6e6e6)";
          }}
        >
          + Добавить выезд
        </button>
      ) : (
        <>
          {items.map((v) => (
            <VisitRow key={v.id} v={v} onClick={() => setModalTarget(v)}/>
          ))}
          <button
            type="button"
            onClick={() => setModalTarget("new")}
            style={{
              alignSelf: "flex-start",
              padding: "6px 12px", fontSize: 12, fontWeight: 500,
              background: "transparent", color: "var(--mute)",
              border: "1px dashed var(--line, #e6e6e6)",
              borderRadius: 6, cursor: "pointer",
              fontFamily: "inherit",
              transition: "color 100ms, border-color 100ms",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.color = "var(--brick, #a63a1f)";
              (e.currentTarget as HTMLButtonElement).style.borderColor = "var(--brick, #a63a1f)";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.color = "var(--mute)";
              (e.currentTarget as HTMLButtonElement).style.borderColor = "var(--line, #e6e6e6)";
            }}
          >
            + ещё выезд
          </button>
        </>
      )}

      {modalTarget && (
        <Suspense fallback={<div className="modal-backdrop"><Spinner label="Загрузка формы…"/></div>}>
          <VisitEditorModalLazy
            target={modalTarget}
            onClose={() => setModalTarget(null)}
            onSaved={onSaved}
            presetAmoLeadId={amoLeadId}
            presetClientName={clientName}
            presetClientAmoUrl={clientAmoUrl}
          />
        </Suspense>
      )}
    </div>
  );
}


function VisitRow({ v, onClick }: { v: Visit; onClick: () => void }) {
  const dt = v.visit_at ? new Date(v.visit_at) : null;
  const dateStr = dt
    ? dt.toLocaleDateString("ru", { day: "2-digit", month: "short", year: "2-digit" })
    : "— без даты —";
  const timeStr = dt
    ? dt.toLocaleTimeString("ru", { hour: "2-digit", minute: "2-digit" })
    : null;
  const typeC = visitTypeColor(v.visit_type);
  const statusC = visitStatusColor(v.status);

  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: "flex", alignItems: "center", gap: 10,
        padding: "10px 12px", fontSize: 13, textAlign: "left",
        background: "#fff",
        border: "1px solid var(--line, #e6e6e6)", borderRadius: 8,
        cursor: "pointer", fontFamily: "inherit",
        borderLeft: `3px solid ${statusC.accent}`,
        transition: "background 100ms, border-color 100ms, box-shadow 100ms",
      }}
      onMouseEnter={(e) => {
        const el = e.currentTarget as HTMLButtonElement;
        el.style.background = "#fafafa";
        el.style.borderColor = "var(--brick, #a63a1f)";
        el.style.borderLeftColor = statusC.accent;
      }}
      onMouseLeave={(e) => {
        const el = e.currentTarget as HTMLButtonElement;
        el.style.background = "#fff";
        el.style.borderColor = "var(--line, #e6e6e6)";
        el.style.borderLeftColor = statusC.accent;
      }}
    >
      {/* Дата + время блоком */}
      <div style={{
        display: "flex", flexDirection: "column", lineHeight: 1.2,
        minWidth: 78, flexShrink: 0,
      }}>
        <span style={{
          fontSize: 13, fontWeight: 600, color: "var(--ink)",
          fontVariantNumeric: "tabular-nums",
          whiteSpace: "nowrap",
        }}>{dateStr}</span>
        {timeStr && (
          <span style={{
            fontSize: 11, color: "var(--mute)",
            fontVariantNumeric: "tabular-nums",
          }}>{timeStr}</span>
        )}
      </div>

      {/* Чип типа выезда */}
      <span style={{
        padding: "2px 8px", borderRadius: 10,
        background: typeC.bg, color: typeC.fg,
        fontSize: 11, fontWeight: 600, whiteSpace: "nowrap",
        flexShrink: 0,
      }}>{visitTypeLabel(v.visit_type)}</span>

      {/* Инженер + менеджер */}
      <div style={{
        display: "flex", flexDirection: "column", gap: 1, minWidth: 0,
        flex: 1,
      }}>
        {v.engineer_name && (
          <span style={{
            fontSize: 12, color: "var(--ink-2, #444)",
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }} title={`Инженер: ${v.engineer_name}`}>
            🛠 {v.engineer_name}
          </span>
        )}
        {v.manager_name && (
          <span style={{
            fontSize: 11, color: "var(--mute)",
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }} title={`Менеджер: ${v.manager_name}`}>
            👤 {v.manager_name}
          </span>
        )}
        {!v.engineer_name && !v.manager_name && (
          <span style={{ fontSize: 12, color: "var(--mute)", fontStyle: "italic" }}>
            — без инженера —
          </span>
        )}
      </div>

      {/* Статус */}
      <span style={{
        padding: "2px 8px", borderRadius: 10,
        background: statusC.bg, color: statusC.fg,
        fontSize: 11, fontWeight: 600, whiteSpace: "nowrap",
        flexShrink: 0,
      }}>{visitStatusLabel(v.status)}</span>
    </button>
  );
}


function visitStatusColor(s: string): { bg: string; fg: string; accent: string } {
  return {
    scheduled: { bg: "#fff8e1", fg: "#7c5300", accent: "#f9a825" },
    completed: { bg: "#e8f5e9", fg: "#2e7d32", accent: "#4fae4e" },
    cancelled: { bg: "#f5f5f5", fg: "#616161", accent: "#9e9e9e" },
    no_show:   { bg: "#ffebee", fg: "#c62828", accent: "#ef5350" },
  }[s] || { bg: "#f5f5f5", fg: "#616161", accent: "#9e9e9e" };
}


function visitTypeLabel(t: string): string {
  return { measure: "Замер", meeting: "Встреча", install: "Монтаж", other: "Прочее" }[t] || t;
}
function visitTypeColor(t: string): { bg: string; fg: string } {
  return {
    measure:  { bg: "#e3f2fd", fg: "#1565c0" },
    meeting:  { bg: "#f3e5f5", fg: "#6a1b9a" },
    install:  { bg: "#e8f5e9", fg: "#2e7d32" },
    other:    { bg: "#f5f5f5", fg: "#616161" },
  }[t] || { bg: "#f5f5f5", fg: "#616161" };
}
function visitStatusLabel(s: string): string {
  return { scheduled: "Запланирован", completed: "Состоялся", cancelled: "Отменён", no_show: "Не явился" }[s] || s;
}
